import React, { FC } from 'react';
import { ChartDataFunction, Line } from 'react-chartjs-2';
import { useTranslation } from '@locales/useTranslation.hook';
import { chartOptions, ChartProps, COLUMN_WIDTH, DEFAULT_HORIZONTAL_PADDING } from '@meteo/business/meteo.chart.utils';
import { PlcColor } from '@styles/colors';
import { formatNumberWithComma } from '@utils/number';
import { ChartData } from 'chart.js';

import 'chartjs-plugin-datalabels';

function buildColorFromTemp(temp: number) {
  if (temp < 0) {
    return PlcColor.BLUE_LAND;
  }
  if (temp >= 25) {
    return PlcColor.RED_PEPPER;
  }
  return PlcColor.GREEN;
}

const MeteoTemperatureChartCanvas: FC<ChartProps> = ({ chartData, chartWidth }) => {
  const { t } = useTranslation(['meteo']);
  const chartColor = PlcColor.GREEN;
  const horizontalPadding = DEFAULT_HORIZONTAL_PADDING + COLUMN_WIDTH / 2;
  const data: ChartDataFunction<ChartData> = (canvas: HTMLElement) => {
    const ctx = (canvas as HTMLCanvasElement).getContext('2d');
    let gradient: CanvasGradient | undefined;
    if (ctx && chartData.length > 1) {
      const grad = ctx.createLinearGradient(horizontalPadding, 0, chartWidth - horizontalPadding, 0);
      for (let i = 0; i < chartData.length - 1; i += 1) {
        const curTemp = chartData[i];
        const nextTemp = chartData[i + 1];
        grad.addColorStop(i / (chartData.length - 1), buildColorFromTemp(curTemp));
        const offset = nextTemp - curTemp;
        if (offset) {
          for (let j = 1; j < Math.abs(offset); j += 1) {
            const newTemp = offset > 0 ? curTemp + j : curTemp - j;
            const gradPercent = (i + j / Math.abs(offset)) / (chartData.length - 1);
            grad.addColorStop(gradPercent, buildColorFromTemp(newTemp));
          }
        }
      }
      grad.addColorStop(1, buildColorFromTemp(chartData[chartData.length - 1]));
      gradient = grad;
    }

    return {
      datasets: [
        {
          backgroundColor: gradient || chartColor,
          borderColor: gradient || chartColor,
          borderWidth: 2,
          data: chartData,
          fill: false,
        },
      ],
      labels: chartData.map((temp) => t('meteo.celcius', { value: formatNumberWithComma(temp, 1) }) as string),
    };
  };

  const options = {
    ...chartOptions({ bottomPadding: 5, horizontalPadding, labelOffset: 12 }),
    beginAtZero: false,
  };

  return <Line data={data} height={100} options={options} width={chartWidth} />;
};

export { MeteoTemperatureChartCanvas };
